import React, { useState, useCallback, useEffect } from 'react';
import { SearchBar } from '../../search/components/SearchBar';
import { DisplayMode } from '../../../types/enum/display-mode';
import type { SearchSuggestion } from '../../search/view-models/search-suggestion';
import type { SearchSuggestionGroup } from '../../search/view-models/search-suggestion';
import { isCategorySuggestion } from '../../search/utils/type-guards';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';

export interface HeaderSearchTrackingConfig {
    onPerformSearch?: (query: string) => void;
    onSearchSuggestionClick?: (query: string) => void;
    onRecentSearchClick?: (query: string) => void;
    onSuggestedCategoryClick?: (title: string) => void;
    onSearchTrendingClick?: (title: string) => void;
}

export interface HeaderSearchProps {
    onNavigate: (url: string) => void;
    displayMode: DisplayMode;
    placeholderTexts: string[];
    menuColor?: 'light' | 'dark';
    trackingConfig?: HeaderSearchTrackingConfig;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
}

/**
 * Header search component that provides search functionality in the website header.
 * Uses the SearchBar component with the 'header' variant for consistent styling.
 * Integrates with localStorage via useRecentlySearched hook for recently searched terms.
 */
export const HeaderSearch = ({
    onNavigate,
    displayMode,
    placeholderTexts,
    menuColor = 'dark',
    trackingConfig,
    fetchSuggestions,
    suggestionData,
}: HeaderSearchProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [recentlySearched, setRecentlySearched] = useState<string[]>([]);
    const [suggestionGroups, setSuggestionGroups] = useState<
        SearchSuggestionGroup[]
    >([]);

    // Helper function to determine search category based on current page
    const getCurrentPageCategory = useCallback((): string => {
        if (typeof window === 'undefined') return '';

        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/projects')) return 'projects';
        if (currentPath.startsWith('/professionals')) return 'professionals';
        if (currentPath.startsWith('/products')) return 'products';
        if (currentPath.startsWith('/articles')) return 'articles';
        return '';
    }, []);

    // Helper function to build search URL with category context
    const buildSearchUrl = useCallback(
        (searchTerm: string): string => {
            const category = getCurrentPageCategory();
            const searchParams = new URLSearchParams({ search: searchTerm });
            if (category) {
                searchParams.set('category', category);
            }
            return `/search?${searchParams.toString()}`;
        },
        [getCurrentPageCategory]
    );

    // Set initial search query from URL when component mounts
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const currentSearchQuery = urlParams.get('search') || '';
            // Only set searchQuery if there's a valid search term
            if (currentSearchQuery.trim()) {
                setSearchQuery(currentSearchQuery);
            }
        }
    }, []);

    // Update search query when URL changes (for both programmatic and browser navigation)
    useEffect(() => {
        let lastUrl = window.location.href;

        const checkForUrlChange = () => {
            if (
                typeof window !== 'undefined' &&
                window.location.href !== lastUrl
            ) {
                lastUrl = window.location.href;
                const urlParams = new URLSearchParams(window.location.search);
                const currentSearchQuery = urlParams.get('search') || '';
                // Only set searchQuery if there's a valid search term
                if (currentSearchQuery.trim()) {
                    setSearchQuery(currentSearchQuery);
                } else {
                    setSearchQuery('');
                }
            }
        };

        // Check for URL changes periodically
        const interval = setInterval(checkForUrlChange, 100);

        // Also listen to popstate for browser navigation
        const handlePopState = () => checkForUrlChange();
        window.addEventListener('popstate', handlePopState);

        return () => {
            clearInterval(interval);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Load recently searched from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const stored = localStorage.getItem('recently-searched');
                if (stored) {
                    const parsed: unknown = JSON.parse(stored);
                    setRecentlySearched(
                        Array.isArray(parsed) &&
                            parsed.every(
                                (item): item is string =>
                                    typeof item === 'string'
                            )
                            ? parsed
                            : []
                    );
                }
            } catch (error) {
                console.warn('Failed to load recently searched:', error);
            }
        }
    }, []);

    // Update suggestion groups when fetch data changes
    useEffect(() => {
        if (!suggestionData) {
            setSuggestionGroups([]);
            return;
        }

        const suggestedKeywords: SearchSuggestionGroup = {
            options: suggestionData.keywords || [],
            type: 'keyword',
        };

        const suggestedCategories: SearchSuggestionGroup = {
            options: suggestionData.categories || [],
            type: 'categories',
        };

        const groups: SearchSuggestionGroup[] = [
            suggestedKeywords,
            suggestedCategories,
        ];

        setSuggestionGroups(groups);
    }, [suggestionData]);

    // Add recent search function
    const addRecentSearch = useCallback((search: string) => {
        if (!search || typeof window === 'undefined') return;

        setRecentlySearched((prev) => {
            const filtered = prev.filter((item) => item !== search);
            const updated = [search, ...filtered].slice(0, 5); // Keep max 5 items

            try {
                localStorage.setItem(
                    'recently-searched',
                    JSON.stringify(updated)
                );
            } catch (error) {
                console.warn('Failed to save recently searched:', error);
            }

            return updated;
        });
    }, []);

    // Clear history function
    const clearHistory = useCallback(() => {
        setRecentlySearched([]);
        if (typeof window !== 'undefined') {
            try {
                localStorage.removeItem('recently-searched');
            } catch (error) {
                console.warn('Failed to clear recently searched:', error);
            }
        }
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newQuery = e.target.value;
            setSearchQuery(newQuery);

            // Fetch suggestions when query length is 2 or more characters
            if (newQuery.trim().length >= 2 && fetchSuggestions) {
                fetchSuggestions(newQuery);
            } else {
                // Clear suggestions when query is too short
                setSuggestionGroups([]);
            }
        },
        [fetchSuggestions]
    );

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (searchQuery.trim()) {
                    // Track PerformSearch event
                    trackingConfig?.onPerformSearch?.(searchQuery.trim());

                    const url = buildSearchUrl(searchQuery.trim());
                    addRecentSearch(searchQuery.trim());
                    onNavigate(url);
                }
            }
        },
        [
            searchQuery,
            onNavigate,
            addRecentSearch,
            trackingConfig,
            buildSearchUrl,
        ]
    );

    const handleSearchSubmit = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (value: string | SearchSuggestion, _isCategory: boolean) => {
            if (typeof value === 'string' && value.trim()) {
                // Track PerformSearch event
                trackingConfig?.onPerformSearch?.(value.trim());

                const url = buildSearchUrl(value.trim());
                addRecentSearch(value.trim());
                onNavigate(url);
            }
        },
        [onNavigate, addRecentSearch, trackingConfig, buildSearchUrl]
    );

    const handleSearchSuggestionClick = useCallback(
        (suggestion: SearchSuggestion | string) => {
            if (typeof suggestion === 'string') {
                // Track SearchSuggestionClick event
                trackingConfig?.onSearchSuggestionClick?.(suggestion);

                const url = buildSearchUrl(suggestion);
                addRecentSearch(suggestion);
                onNavigate(url);
            } else if (isCategorySuggestion(suggestion)) {
                // Track SearchSuggestedCategoryClick event
                trackingConfig?.onSuggestedCategoryClick?.(suggestion.title);

                addRecentSearch(suggestion.title);
                onNavigate(suggestion.link);
            } else {
                // Track SearchSuggestionClick event
                trackingConfig?.onSearchSuggestionClick?.(suggestion);

                const url = buildSearchUrl(suggestion);
                addRecentSearch(suggestion);
                onNavigate(url);
            }
        },
        [onNavigate, addRecentSearch, trackingConfig, buildSearchUrl]
    );

    const handleRecentlySearchedClick = useCallback(
        (search: string) => {
            // Track RecentSearchClick event
            trackingConfig?.onRecentSearchClick?.(search);

            const url = buildSearchUrl(search);
            setSearchQuery(search);
            onNavigate(url);
        },
        [onNavigate, trackingConfig, buildSearchUrl]
    );

    const clearSearchQuery = useCallback(() => {
        setSearchQuery('');
    }, []);

    return (
        <SearchBar
            placeholderTexts={placeholderTexts}
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onSearchSubmit={handleSearchSubmit}
            clearSearchQuery={clearSearchQuery}
            displayMode={displayMode}
            variant="header"
            hideClearButton={false}
            hideSubmitButton={false}
            disableDropdown={false}
            suggestionGroups={suggestionGroups}
            onSearchSuggestionClick={handleSearchSuggestionClick}
            recentlySearched={recentlySearched}
            clearHistory={clearHistory}
            onRecentlySearchedClick={handleRecentlySearchedClick}
            menuColor={menuColor}
        />
    );
};
